import React, { useEffect, useState } from "react";
import {
  Typography,
  Button,
  Card,
  Checkbox,
  Table,
  Empty,
  Space,
  Input,
  DatePicker,
  InputNumber,
  Select
} from "antd";
import { PlusOutlined, SaveOutlined } from "@ant-design/icons";
import dayjs from "dayjs";
import styled from "styled-components";

import { useAppDispatch, useAppSelector } from "../hooks/useAppDispatch";
import {
  fetchCampaigns,
  addKolToCampaignAction,
  removeKolFromCampaignAction,
  EmailCampaign as EmailCampaignType,
  CampaignKol,
} from "../store/slices/emailCampaignSlice";
import { fetchKols } from "../store/slices/kolSlice";
import { RootState } from "../store";

// 解构出 antd Typography 组件中的 Title
const { Title } = Typography;

// 顶部操作区样式
const TopBar = styled.div`
  display: flex;
  justify-content: space-between;
  align-items: center;
  margin-bottom: 16px;
`;

// 每个 Campaign 卡片的样式
const CampaignCard = styled(Card)`
  margin-bottom: 16px;
`;

// 新建 KOL 时使用的数据类型
interface NewKolData {
  kolName: string; // 或根据需要存 kolId
  dateTime: string; // ISO 格式字符串
  numberOfPeople: number;
  treatment: string;
}

// ...（省略 import 同之前完全一样）
const EmailCampaign: React.FC = () => {
  const dispatch = useAppDispatch();
  const { campaigns: fetchedCampaigns } = useAppSelector(
    (state: RootState) => state.emailCampaign
  );
  const { kols } = useAppSelector((state: RootState) => state.kol);

  const [localCampaigns, setLocalCampaigns] = useState<EmailCampaignType[]>([]);
  const campaigns = [...fetchedCampaigns, ...localCampaigns];
  const [checkedCampaigns, setCheckedCampaigns] = useState<string[]>([]);
  const [addingKolState, setAddingKolState] = useState<{
    [campaignId: string]: { isAdding: boolean; data: NewKolData };
  }>({});

  const [selectingKolCampaignId, setSelectingKolCampaignId] = useState<string | null>(null);
  const [selectedKolId, setSelectedKolId] = useState<string | null>(null);

  // 🆕 campaign title edit state
  const [editingCampaignId, setEditingCampaignId] = useState<string | null>(null);
  const [editingCampaignName, setEditingCampaignName] = useState<string>("");

  useEffect(() => {
    dispatch(fetchCampaigns());
    dispatch(fetchKols());
  }, [dispatch]);

  const handleSaveAll = () => {
    console.log("Save All clicked. Checked campaigns:", checkedCampaigns);
  };

  const handleAddCampaign = () => {
    const newCampaign: EmailCampaignType = {
      id: "new-" + Date.now(),
      name: "Untitled Campaign",
      description: "",
      status: "draft",
      startDate: "",
      endDate: "",
      kols: [],
      createdAt: new Date().toISOString(),
      updatedAt: new Date().toISOString(),
    };
    setLocalCampaigns([...localCampaigns, newCampaign]);
  };

  const handleCampaignNameSave = (campaignId: string) => {
    setLocalCampaigns((prev) =>
      prev.map((c) =>
        c.id === campaignId ? { ...c, name: editingCampaignName } : c
      )
    );
    setEditingCampaignId(null);
  };

  const handleStartAddingKol = (campaignId: string) => {
    setAddingKolState((prev) => ({
      ...prev,
      [campaignId]: {
        isAdding: true,
        data: {
          kolName: "",
          dateTime: "",
          numberOfPeople: 1,
          treatment: "",
        },
      },
    }));
  };

  const handleCancelAddKol = (campaignId: string) => {
    setAddingKolState((prev) => ({
      ...prev,
      [campaignId]: {
        isAdding: false,
        data: prev[campaignId]?.data || {
          kolName: "",
          dateTime: "",
          numberOfPeople: 1,
          treatment: "",
        },
      },
    }));
  };

  const handleFieldChange = (
    campaignId: string,
    field: keyof NewKolData,
    value: string | number
  ) => {
    setAddingKolState((prev) => {
      const old = prev[campaignId] || {
        isAdding: false,
        data: {
          kolName: "",
          dateTime: "",
          numberOfPeople: 1,
          treatment: "",
        },
      };
      return {
        ...prev,
        [campaignId]: {
          ...old,
          data: {
            ...old.data,
            [field]: value,
          },
        },
      };
    });
  };

  const handleSaveKol = (campaignId: string) => {
    const kolInfo = addingKolState[campaignId]?.data;
    if (!kolInfo) return;

    dispatch(
      addKolToCampaignAction({
        campaignId,
        kolData: {
          kolId: kolInfo.kolName,
          dateTime: kolInfo.dateTime,
          numberOfPeople: kolInfo.numberOfPeople,
          treatment: kolInfo.treatment,
        },
      })
    );

    setLocalCampaigns((prevCampaigns) =>
      prevCampaigns.map((camp) =>
        camp.id === campaignId
          ? {
              ...camp,
              kols: [
                ...camp.kols,
                {
                  id: "local-" + Date.now(),
                  kol: { name: kolInfo.kolName },
                } as CampaignKol,
              ],
            }
          : camp
      )
    );

    setAddingKolState((prev) => ({
      ...prev,
      [campaignId]: { isAdding: false, data: { ...kolInfo } },
    }));
  };

  const handleRemoveKol = (campaignId: string, kolCampaignId: string) => {
    dispatch(
      removeKolFromCampaignAction({
        campaignId,
        kolCampaignId,
      })
    );
    setLocalCampaigns((prevCampaigns) =>
      prevCampaigns.map((camp) =>
        camp.id === campaignId
          ? {
              ...camp,
              kols: camp.kols.filter((kol) => kol.id !== kolCampaignId),
            }
          : camp
      )
    );
  };

  const getKolColumns = (campaignId: string) => [
    {
      title: "KOL Name",
      dataIndex: "kolName",
      key: "kolName",
      render: (val: any, record: CampaignKol | { isNew: true }) => {
        if ("isNew" in record) {
          const newKol = addingKolState[campaignId]?.data;
          return (
            <Input
              placeholder="KOL Name"
              value={newKol?.kolName || ""}
              onChange={(e) =>
                handleFieldChange(campaignId, "kolName", e.target.value)
              }
            />
          );
        } else {
          return record.kol.name;
        }
      },
    },
    {
      title: "Date & Time",
      dataIndex: "dateTime",
      key: "dateTime",
      render: (val: any, record: CampaignKol | { isNew: true }) => {
        if ("isNew" in record) {
          const newKol = addingKolState[campaignId]?.data;
          return (
            <DatePicker
              showTime
              style={{ width: "100%" }}
              placeholder="Date & Time"
              value={newKol?.dateTime ? dayjs(newKol.dateTime) : null}
              onChange={(date) =>
                handleFieldChange(
                  campaignId,
                  "dateTime",
                  date ? date.toISOString() : ""
                )
              }
            />
          );
        } else {
          return dayjs(val).format("YYYY-MM-DD HH:mm");
        }
      },
    },
    {
      title: "No. of People",
      dataIndex: "numberOfPeople",
      key: "numberOfPeople",
      render: (val: any, record: CampaignKol | { isNew: true }) => {
        if ("isNew" in record) {
          const newKol = addingKolState[campaignId]?.data;
          return (
            <InputNumber
              min={1}
              style={{ width: "100%" }}
              placeholder="No of People"
              value={newKol?.numberOfPeople || 1}
              onChange={(value) =>
                handleFieldChange(campaignId, "numberOfPeople", value || 1)
              }
            />
          );
        } else {
          return val;
        }
      },
    },
    {
      title: "Treatment",
      dataIndex: "treatment",
      key: "treatment",
      render: (val: any, record: CampaignKol | { isNew: true }) => {
        if ("isNew" in record) {
          const newKol = addingKolState[campaignId]?.data;
          return (
            <Input
              placeholder="Treatment"
              value={newKol?.treatment || ""}
              onChange={(e) =>
                handleFieldChange(campaignId, "treatment", e.target.value)
              }
            />
          );
        } else {
          return val;
        }
      },
    },
    {
      title: "Actions",
      key: "actions",
      render: (_: any, record: CampaignKol | { isNew: true }) => {
        if ("isNew" in record) {
          return (
            <Space>
              <Button
                type="primary"
                onClick={() => handleSaveKol(campaignId)}
              >
                Save
              </Button>
              <Button danger onClick={() => handleCancelAddKol(campaignId)}>
                Cancel
              </Button>
            </Space>
          );
        } else {
          return (
            <Button
              danger
              onClick={() => handleRemoveKol(campaignId, record.id)}
            >
              Remove
            </Button>
          );
        }
      },
    },
  ];

  return (
    <div style={{ padding: 16 }}>
      <TopBar>
        <Title level={2}>Campaign Email</Title>
        <Space>
          <Button icon={<SaveOutlined />} onClick={handleSaveAll}>
            Save All
          </Button>
          <Button
            icon={<PlusOutlined />}
            type="primary"
            onClick={handleAddCampaign}
          >
            Add Campaign
          </Button>
        </Space>
      </TopBar>

      {campaigns.map((campaign) => {
        const isChecked = checkedCampaigns.includes(campaign.id);
        const isAdding = addingKolState[campaign.id]?.isAdding;

        let tableData: (CampaignKol | { isNew: true })[] = [...campaign.kols];
        if (isAdding) {
          tableData.push({ isNew: true });
        }

        return (
          <CampaignCard
            key={campaign.id}
            title={
              <Space>
                <Checkbox
                  checked={isChecked}
                  onChange={(e) =>
                    setCheckedCampaigns((prev) =>
                      e.target.checked
                        ? [...prev, campaign.id]
                        : prev.filter((id) => id !== campaign.id)
                    )
                  }
                />
                {editingCampaignId === campaign.id ? (
                  <Input
                    size="small"
                    value={editingCampaignName}
                    onChange={(e) =>
                      setEditingCampaignName(e.target.value)
                    }
                    onBlur={() => handleCampaignNameSave(campaign.id)}
                    onPressEnter={() => handleCampaignNameSave(campaign.id)}
                    autoFocus
                  />
                ) : (
                  <div
                    onClick={() => {
                      setEditingCampaignId(campaign.id);
                      setEditingCampaignName(campaign.name);
                    }}
                    style={{ fontWeight: 500 }}
                  >
                    {campaign.name}
                  </div>
                )}
              </Space>
            }
            extra={
              <Space>
                {!isAdding && (
                  <Button
                    type="primary"
                    onClick={() => handleStartAddingKol(campaign.id)}
                  >
                    Add KOL
                  </Button>
                )}
                <Button
                  onClick={() => setSelectingKolCampaignId(campaign.id)}
                >
                  Select Existing KOL
                </Button>
              </Space>
            }
          >
            {selectingKolCampaignId === campaign.id && (
              <Space style={{ marginBottom: 16 }}>
                <Select
                  showSearch
                  placeholder="Select a KOL"
                  style={{ width: 200 }}
                  value={selectedKolId || undefined}
                  onChange={(val) => setSelectedKolId(val)}
                >
                  {kols.map((kol) => (
                    <Select.Option key={kol.id} value={kol.id}>
                      {kol.name}
                    </Select.Option>
                  ))}
                </Select>
                <Button
                  type="primary"
                  disabled={!selectedKolId}
                  onClick={() => {
                    if (selectedKolId) {
                      dispatch(
                        addKolToCampaignAction({
                          campaignId: campaign.id,
                          kolData: {
                            kolId: selectedKolId,
                            dateTime: new Date().toISOString(),
                            numberOfPeople: 1,
                            treatment: "",
                          },
                        })
                      );
                      setSelectedKolId(null);
                      setSelectingKolCampaignId(null);
                    }
                  }}
                >
                  Add
                </Button>
                <Button onClick={() => setSelectingKolCampaignId(null)}>
                  Cancel
                </Button>
              </Space>
            )}

            {tableData.length > 0 ? (
              <Table
                columns={getKolColumns(campaign.id)}
                dataSource={tableData}
                rowKey={(record: any) =>
                  "isNew" in record ? "new" : record.id
                }
                pagination={false}
              />
            ) : (
              <Empty description="No KOLs in this campaign" />
            )}
          </CampaignCard>
        );
      })}

      {campaigns.length === 0 && (
        <Empty description="No campaigns found" style={{ marginTop: 48 }} />
      )}
    </div>
  );
};

export default EmailCampaign;
